import { formatDate } from "@/utils/services";
import { inCallSegmentForMode, joinWindowFor } from "@/utils/meeting";
import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";

const statusClasses = {
  completed: "bg-green-100 text-green-700",
  pending: "bg-yellow-100 text-yellow-700",
  cancelled: "bg-red-100 text-red-700",
  expired: "bg-gray-100 text-gray-700",
};

const formatDuration = (durationInSeconds) => {
  const totalSeconds = parseInt(durationInSeconds, 10);

  if (totalSeconds === 0) {
    return "0 min 0 sec";
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes} min ${seconds} sec`;
};

const AppointmentTable = ({ paginatedData }) => {
  if (paginatedData.length === 0) {
    return <div>No appointments found</div>;
  }

  return (
    <div className="w-full p-6 overflow-y-auto bg-white rounded-2xl">
      <div className="flex flex-col h-full">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse rounded-lg">
            <thead>
              <tr className="text-left text-sm font-semibold text-gray-700 bg-gray-100 sticky top-0 z-10">
                <th className="p-3">Name</th>
                <th className="p-3">Type</th>
                <th className="p-3">Mode</th>
                <th className="p-3">Date/Time</th>
                <th className="p-3">Status</th>
                <th className="p-3">Duration</th>
                <th className="p-3">Rate/Min</th>
                <th className="p-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((consultation) => {
                const { canJoin, hint } = joinWindowFor(
                  consultation.date,
                  consultation.status
                );
                const isConsultNowAvailable = canJoin;
                const disabledTitle = !consultation.meetingId
                  ? "Meeting link unavailable. Contact support."
                  : hint || "";
                return (
                  <tr
                    key={consultation.id}
                    className="border-b border-gray-200 text-sm"
                  >
                    <td className="flex items-center space-x-3 p-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-500 text-white font-semibold">
                        {consultation.name
                          .split(" ")
                          .map((word) => word[0])
                          .join("")
                          .toUpperCase()}
                      </div>
                      <div>
                        <Link
                          to={`/schedule/appointments/details/${consultation.id}`}
                          className="hover:underline"
                          title="View appointment details"
                        >
                          <p className="font-semibold text-gray-900 hover:text-orange-600">
                            {consultation.name}
                          </p>
                          <p className="text-xs text-gray-500 hover:text-orange-500">
                            {String(consultation.reference || "").slice(-8)}
                          </p>
                        </Link>
                      </div>
                    </td>
                    <td className="p-3">{consultation.type}</td>
                    <td className="p-3 font-semibold">{consultation.mode}</td>
                    <td className="p-3">{formatDate(consultation.date)}</td>
                    <td className="p-3">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-semibold ${statusClasses[consultation.status]
                          }`}
                      >
                        {consultation.status}
                      </span>
                    </td>
                    <td className="p-3">{(consultation.status === "pending" || consultation.status === "cancelled") ? formatDuration(0) : formatDuration(consultation.duration)}</td>
                    <td className="p-3 font-bold">{consultation.rate}</td>
                    <td className="p-3">
                      {isConsultNowAvailable && consultation.meetingId ? (
                        <Link
                          to={`/connect/schedule/${inCallSegmentForMode(
                            consultation.mode
                          )}/${consultation.meetingId}`}
                        >
                          <button className="rounded-full bg-orange-600 px-4 py-2 text-sm font-bold text-white hover:bg-orange-700">
                            Consult Now
                          </button>
                        </Link>
                      ) : (
                        <button
                          disabled
                          className="rounded-full bg-orange-300 px-4 py-2 text-sm font-bold text-white cursor-not-allowed"
                          title={disabledTitle}
                        >
                          Consult Now
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

AppointmentTable.propTypes = {
  paginatedData: PropTypes.arrayOf(
    PropTypes.shape({
      // Mongo ObjectId stringified — was incorrectly typed as number
      // before, which logged a propTypes warning on every render.
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      reference: PropTypes.string,
      type: PropTypes.string.isRequired,
      mode: PropTypes.string.isRequired,
      // moment().toDate() in the index.jsx mapper returns a Date object,
      // not a string. instanceOf(Date) is the accurate check.
      date: PropTypes.instanceOf(Date).isRequired,
      status: PropTypes.oneOf(["completed", "pending", "cancelled", "expired"])
        .isRequired,
      // duration upstream is `d.duration || 0` — a number, not a string.
      duration: PropTypes.number.isRequired,
      rate: PropTypes.string.isRequired,
      meetingId: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default AppointmentTable;
